import type { ApiClient } from '../../core/api-client.js';
import { ApiError } from '../../core/api-client.js';
import type {
  ClienteAutenticado,
  Pedido,
  PedidoComItens,
  PedidoEventoPublico,
  Garantia,
  Ticket,
  NovoTicket,
} from '@pgcustom/shared-types';

interface RespostaLoginPortal {
  cliente: ClienteAutenticado;
  expiraEm: string;
}

interface RespostaMePortal {
  cliente: ClienteAutenticado;
}

interface RespostaPedidos {
  pedidos: Pedido[];
}

interface RespostaPedido {
  pedido: PedidoComItens;
}

interface RespostaPedidoEventos {
  eventos: PedidoEventoPublico[];
}

interface RespostaGarantias {
  garantias: Garantia[];
}

interface RespostaSolicitacoes {
  solicitacoes: Ticket[];
}

interface RespostaSolicitacao {
  solicitacao: Ticket;
}

/**
 * Adapter do Portal do Cliente no frontend — único ponto que conhece o
 * formato de `/api/v1/portal-clientes/*`
 * (`apps/api/src/modules/cliente-portal/cliente-portal.routes.ts`).
 * Recebe seu próprio `ApiClient` (nunca o mesmo usado pelo ERP interno
 * nem o do Portal do Parceiro) — a sessão do cliente vive num cookie
 * próprio (`pg_portal_cliente_session`) e um 401 aqui nunca deve
 * derrubar uma sessão de `identidade.usuarios`/Portal do Parceiro que
 * porventura exista na mesma aba, nem vice-versa (mesma fronteira já
 * resolvida no backend, espelhada aqui).
 */
export class PortalClienteApi {
  constructor(private readonly api: ApiClient) {}

  async login(email: string, senha: string): Promise<ClienteAutenticado> {
    const resposta = await this.api.post<RespostaLoginPortal>('/portal-clientes/login', { email, senha });
    return resposta.cliente;
  }

  async logout(): Promise<void> {
    await this.api.post<void>('/portal-clientes/logout');
  }

  /** `null` quando não há sessão válida (401) — estado esperado de um visitante não autenticado, não um erro. */
  async obterClienteAtual(): Promise<ClienteAutenticado | null> {
    try {
      const resposta = await this.api.get<RespostaMePortal>('/portal-clientes/me');
      return resposta.cliente;
    } catch (erro) {
      if (erro instanceof ApiError && erro.status === 401) return null;
      throw erro;
    }
  }

  async meusPedidos(): Promise<Pedido[]> {
    const resposta = await this.api.get<RespostaPedidos>('/portal-clientes/me/pedidos');
    return resposta.pedidos;
  }

  async meuPedido(id: string): Promise<PedidoComItens> {
    const resposta = await this.api.get<RespostaPedido>(`/portal-clientes/me/pedidos/${id}`);
    return resposta.pedido;
  }

  /** Timeline pública do pedido (status + mensagem da PG Custom) — nunca inclui nota interna, nem no tipo. */
  async meuPedidoEventos(id: string): Promise<PedidoEventoPublico[]> {
    const resposta = await this.api.get<RespostaPedidoEventos>(`/portal-clientes/me/pedidos/${id}/eventos`);
    return resposta.eventos;
  }

  async minhasGarantias(): Promise<Garantia[]> {
    const resposta = await this.api.get<RespostaGarantias>('/portal-clientes/me/garantias');
    return resposta.garantias;
  }

  async minhasSolicitacoes(): Promise<Ticket[]> {
    const resposta = await this.api.get<RespostaSolicitacoes>('/portal-clientes/me/solicitacoes');
    return resposta.solicitacoes;
  }

  async abrirSolicitacao(dado: Omit<NovoTicket, 'clienteId'>): Promise<Ticket> {
    const resposta = await this.api.post<RespostaSolicitacao>('/portal-clientes/me/solicitacoes', dado);
    return resposta.solicitacao;
  }
}
